import { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
	createEmSiteRequest,
	deleteEmSiteRequest,
	getEmSitesListRequest,
	updateEmSiteRequest,
} from 'redux/reducers/emManagement/reducer';
import {
	getCreateEmSiteLoading,
	getDeleteEmSiteLoading,
	getEmSitesListData,
	getEmSitesListLoading,
	getEnumsSiteIntegrationType,
	getEnumsSitePlatform,
	getUpdateEmSiteLoading,
} from 'redux/reducers/emManagement/selectors';
// removed unused enum imports
import { TEmEnumItem, TEmSiteItem } from 'redux/reducers/emManagement/types';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { getTableStructure } from './tableStructure';
import { DeleteDialog } from '../../../../../ui/DeleteDialog';

type TMode = 'list' | 'create' | 'edit';

export const EmSitesManageDialog = ({
	open,
	onClose,
	emId,
	initialMode = 'list',
	initialSite,
}: {
	open: boolean;
	onClose: () => void;
	emId: number;
	initialMode?: TMode;
	initialSite?: TEmSiteItem | null;
}) => {
	const dispatch = useDispatch();
	const emSites = useSelector(getEmSitesListData);
	const listLoading = useSelector(getEmSitesListLoading);
	const createLoading = useSelector(getCreateEmSiteLoading);
	const updateLoading = useSelector(getUpdateEmSiteLoading);
	const deleteLoading = useSelector(getDeleteEmSiteLoading);
	const enumsSiteIntegrationType = useSelector(getEnumsSiteIntegrationType);
	const enumsSitePlatform = useSelector(getEnumsSitePlatform);

	// removed debug logs

	const [mode, setMode] = useState<TMode>(initialMode);
	const [editingSite, setEditingSite] = useState<TEmSiteItem | null>(initialSite ?? null);
	const [form, setForm] = useState({
		sitePlatform: '' as string,
		webAddress: '' as string,
		integrationType: '' as string,
		mainSite: false as boolean,
	});
	const [deleteConfirm, setDeleteConfirm] = useState<TEmSiteItem | null>(null);

	useEffect(() => {
		if (open) {
			dispatch(getEmSitesListRequest({ emId }));
		}
	}, [open, dispatch, emId]);

	useEffect(() => {
		setMode(initialMode);
		setEditingSite(initialSite ?? null);
		if (initialMode === 'edit' && initialSite) {
			setForm({
				sitePlatform: initialSite.sitePlatform ?? '',
				webAddress: initialSite.webAddress ?? '',
				integrationType: initialSite.integrationType ?? '',
				mainSite: Boolean(initialSite.mainSite),
			});
		} else if (initialMode === 'create') {
			setForm({ sitePlatform: '', webAddress: '', integrationType: '', mainSite: false });
		}
	}, [initialMode, initialSite, open]);

	const resetToList = () => {
		setMode('list');
		setEditingSite(null);
		setForm({ sitePlatform: '', webAddress: '', integrationType: '', mainSite: false });
		dispatch(getEmSitesListRequest({ emId }));
	};

	const canSubmit = useMemo(() => {
		return Boolean(form.webAddress && form.integrationType && form.sitePlatform);
	}, [form]);

	const handleSave = () => {
		if (!canSubmit) return;
		if (mode === 'create') {
			dispatch(
				createEmSiteRequest({
					emId,
					sitePlatform: form.sitePlatform,
					webAddress: form.webAddress,
					integrationType: form.integrationType,
				}),
			);
			resetToList();
		}
		if (mode === 'edit' && editingSite) {
			dispatch(
				updateEmSiteRequest({
					id: editingSite.id,
					emId,
					sitePlatform: form.sitePlatform,
					webAddress: form.webAddress,
					integrationType: form.integrationType,
					mainSite: form.mainSite,
				}),
			);
			resetToList();
		}
	};

	const handleDelete = () => {
		if (!deleteConfirm) return;
		dispatch(deleteEmSiteRequest({ id: deleteConfirm.id, emId }));
		setDeleteConfirm(null);
		dispatch(getEmSitesListRequest({ emId }));
	};

	const renderList = () => {
		const items = (emSites ?? []).map((s: TEmSiteItem) => ({ ...s, actions: '' }));
		const handleAction = (id: number, action: string) => {
			const s = (emSites ?? []).find(a => a.id === id);
			if (!s) return;
			if (action === 'Edit') {
				setEditingSite(s);
				setForm({
					sitePlatform: s.sitePlatform ?? '',
					webAddress: s.webAddress ?? '',
					integrationType: s.integrationType ?? '',
					mainSite: Boolean(s.mainSite),
				});
				setMode('edit');
			}
			if (action === 'Delete') {
				setDeleteConfirm(s);
			}
		};

		return (
			<Box>
				<EasTableWithoutFilter<TEmSiteItem & { actions: string }>
					tableStructure={getTableStructure(enumsSiteIntegrationType, enumsSitePlatform)}
					items={{
						data: items,
						pageNumber: 1,
						pageSize: (items?.length ?? 0) || 10,
						totalCount: items?.length ?? 0,
					}}
					itemsIsLoading={listLoading || deleteLoading}
					handleAction={handleAction}
					skeletonRows={[1]}
					footerChildren={
						<Box
							sx={{
								width: '100%',
								display: 'flex',
								justifyContent: 'end',
								// backgroundColor: '#bdd2df',
								p: '2px 0px',
							}}
						>
							<Button onClick={onClose}>Close</Button>
						</Box>
					}
				>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<Typography variant="body1" color="primary" fontWeight="bold">
							Sites
						</Typography>
						<Button
							variant="contained"
							size="small"
							onClick={() => {
								setMode('create');
								setForm({ sitePlatform: '', webAddress: '', integrationType: '', mainSite: false });
							}}
						>
							Add site
						</Button>
					</Box>
				</EasTableWithoutFilter>
			</Box>
		);
	};

	const renderForm = () => (
		<Box component="form" onSubmit={e => e.preventDefault()} sx={{ p: 2 }}>
			<Typography variant="h6" color="primary" fontWeight="bold" mb={2}>
				{mode === 'create' ? 'Add site' : 'Edit site'}
			</Typography>
			<Stack spacing={2}>
				<FormControl fullWidth size="small">
					<InputLabel id="platform-label">Platform</InputLabel>
					<Select
						labelId="platform-label"
						label="Platform"
						value={form.sitePlatform}
						onChange={e => setForm(prev => ({ ...prev, sitePlatform: String(e.target.value) }))}
					>
						{enumsSitePlatform?.map((i: TEmEnumItem) => (
							<MenuItem key={i.value} value={i.value}>
								{i.displayName}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<TextField
					size="small"
					label="Website URL"
					value={form.webAddress}
					onChange={e => setForm(prev => ({ ...prev, webAddress: e.target.value }))}
					placeholder="https://example.com"
					fullWidth
				/>
				<FormControl fullWidth size="small">
					<InputLabel id="integration-label">Integration</InputLabel>
					<Select
						labelId="integration-label"
						label="Integration"
						value={form.integrationType}
						onChange={e => setForm(prev => ({ ...prev, integrationType: String(e.target.value) }))}
					>
						{enumsSiteIntegrationType?.map((i: TEmEnumItem) => (
							<MenuItem key={i.value} value={i.value}>
								{i.displayName}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				{/* <FormControlLabel
					control={
						<Checkbox
							checked={form.mainSite}
							onChange={e => setForm(prev => ({ ...prev, mainSite: e.target.checked }))}
						/>
					}
					label="Main site"
				/> */}
				{(createLoading || updateLoading) && (
					<Typography variant="caption" color="text.secondary">
						Saving...
					</Typography>
				)}
			</Stack>
			<Box sx={{ width: '100%', display: 'flex', justifyContent: 'end', mt: 2 }}>
				{/* <Button
									onClick={() => {
										resetToList();
									}}
								>
									Back to list
								</Button> */}
				<Button onClick={onClose}>Cancel</Button>
				<Button disabled={!canSubmit} onClick={handleSave}>
					Save
				</Button>
			</Box>
		</Box>
	);

	return (
		<>
			<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
				{/* <DialogTitle>
					{mode === 'list' && 'Manage sites'}
					{mode === 'create' && 'Create site'}
					{mode === 'edit' && 'Edit site'}
				</DialogTitle> */}
				<DialogContent dividers sx={{ p: 0 }}>
					{mode === 'list' ? renderList() : renderForm()}
				</DialogContent>
				{/* <DialogActions>
					{mode === 'list' ? (
						<Button onClick={onClose}>Close</Button>
					) : (
						<>
							<Button
								onClick={() => {
									resetToList();
								}}
							>
								Back to list
							</Button>
							<Button onClick={onClose}>Cancel</Button>
							<Button variant="contained" disabled={!canSubmit} onClick={handleSave}>
								Save
							</Button>
						</>
					)}
				</DialogActions> */}
			</Dialog>

			<DeleteDialog
				open={Boolean(deleteConfirm)}
				setClose={() => setDeleteConfirm(null)}
				handleDelete={handleDelete}
				text="Are you sure you want to delete this site?"
			/>
		</>
	);
};
